import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Trophy, 
  Calendar,
  MapPin,
  Users,
  ChevronRight,
  Medal,
  Zap
} from 'lucide-react';
import { formatCurrency, formatDate } from '../lib/utils';

const getStatusBadge = (status) => {
  switch (status) {
    case 'registration':
      return { text: 'Anmeldung offen', className: 'bg-tunis-teal text-white' };
    case 'in_progress':
      return { text: 'Läuft', className: 'bg-amber-500 text-white animate-pulse' };
    case 'completed':
      return { text: 'Beendet', className: 'bg-slate-500 text-white' };
    default:
      return { text: status, className: 'bg-slate-500 text-white' };
  }
};

export default function Tournaments() {
  const { user } = useAuth();
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTournaments = async () => {
      try {
        const response = await api.get('/tournaments');
        setTournaments(response.data || []);
      } catch (error) {
        console.error('Failed to fetch tournaments:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTournaments();
  }, []);

  const openTournaments = tournaments.filter(t => t.status === 'registration');
  const activeTournaments = tournaments.filter(t => t.status === 'in_progress');
  const completedTournaments = tournaments.filter(t => t.status === 'completed');

  const TournamentCard = ({ tournament }) => {
    const statusBadge = getStatusBadge(tournament.status);
    
    return (
      <Link to={`/tournaments/${tournament.id}`} data-testid={`tournament-${tournament.id}`}>
        <Card className="hover:shadow-card-hover transition-all duration-300 hover:-translate-y-1 border-slate-200 overflow-hidden">
          <div className="h-2 bg-gradient-to-r from-tunis-blue to-tunis-teal" />
          <CardContent className="p-5">
            <div className="flex justify-between items-start mb-3">
              <Badge className={statusBadge.className}>
                {statusBadge.text}
              </Badge>
              <div className="text-right">
                <div className="text-sm text-muted-foreground">Preisgeld</div>
                <div className="font-bold text-tunis-teal">{formatCurrency(tournament.prize_pool)}</div>
              </div>
            </div>
            
            <h3 className="font-display font-bold text-xl text-tunis-blue mb-2">
              {tournament.name}
            </h3>
            
            <p className="text-sm text-muted-foreground line-clamp-2 mb-4">
              {tournament.description}
            </p>
            
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Calendar className="w-4 h-4" />
                {formatDate(tournament.start_date)}
              </div>
              <div className="flex items-center gap-2 text-muted-foreground">
                <MapPin className="w-4 h-4" />
                {tournament.field_name}
              </div>
              <div className="flex items-center gap-2 text-muted-foreground">
                <Users className="w-4 h-4" />
                {tournament.team_count}/{tournament.max_teams} Teams
              </div>
            </div>

            <div className="mt-4 pt-4 border-t border-slate-100 flex justify-between items-center">
              <div className="text-sm">
                <span className="text-muted-foreground">Eintritt:</span>{' '}
                <span className="font-bold text-tunis-blue">{formatCurrency(tournament.entry_fee)}</span>
              </div>
              <ChevronRight className="w-5 h-5 text-slate-400" />
            </div>
          </CardContent>
        </Card>
      </Link>
    );
  };

  if (loading) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Skeleton className="h-8 w-48 mb-8" />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => (
              <Skeleton key={i} className="h-64 rounded-2xl" />
            ))}
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="tournaments-page">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-amber-400 to-yellow-500 rounded-2xl mb-4 shadow-lg">
            <Trophy className="w-8 h-8 text-white" />
          </div>
          <h1 className="font-display font-bold text-2xl sm:text-3xl text-tunis-blue">
            Turniere
          </h1>
          <p className="text-muted-foreground mt-2">Kämpfe mit deinem Team um Ruhm und Preise</p>
        </div>

        {/* Active Tournaments Banner */}
        {activeTournaments.length > 0 && (
          <Card className="bg-gradient-to-r from-tunis-blue to-tunis-teal text-white border-0 mb-8">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                  <Zap className="w-6 h-6" />
                </div>
                <div>
                  <div className="font-bold text-lg">Laufende Turniere!</div>
                  <div className="text-white/80">
                    {activeTournaments.length} Turnier{activeTournaments.length > 1 ? 'e' : ''} aktuell aktiv
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <Tabs defaultValue="open">
          <TabsList className="mb-6">
            <TabsTrigger value="open" data-testid="tab-open">
              Offen ({openTournaments.length})
            </TabsTrigger>
            <TabsTrigger value="active" data-testid="tab-active">
              Aktiv ({activeTournaments.length})
            </TabsTrigger>
            <TabsTrigger value="completed" data-testid="tab-completed">
              Beendet ({completedTournaments.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="open">
            {openTournaments.length > 0 ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {openTournaments.map((tournament) => (
                  <TournamentCard key={tournament.id} tournament={tournament} />
                ))}
              </div>
            ) : (
              <Card className="border-slate-200">
                <CardContent className="py-12 text-center">
                  <Trophy className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="font-semibold text-lg text-tunis-blue mb-2">
                    Keine offenen Turniere
                  </h3>
                  <p className="text-muted-foreground">
                    Derzeit sind keine Turniere zur Anmeldung verfügbar.
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="active">
            {activeTournaments.length > 0 ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {activeTournaments.map((tournament) => (
                  <TournamentCard key={tournament.id} tournament={tournament} />
                ))}
              </div>
            ) : (
              <Card className="border-slate-200">
                <CardContent className="py-12 text-center">
                  <Trophy className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-muted-foreground">Keine aktiven Turniere</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="completed">
            {completedTournaments.length > 0 ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {completedTournaments.map((tournament) => (
                  <TournamentCard key={tournament.id} tournament={tournament} />
                ))}
              </div>
            ) : (
              <Card className="border-slate-200">
                <CardContent className="py-12 text-center">
                  <Medal className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-muted-foreground">Noch keine beendeten Turniere</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
